"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useAuthStore } from "@/lib/auth-store";
import type { User } from "@/types/auth";

/**
 * Polls `/api/customer/profile` to detect cross-tab authentication.
 * When a magic link is clicked in another tab, cookies are set on the same
 * domain. This hook detects that by polling and auto-redirects to /home.
 */
export function useMagicLinkPolling(enabled: boolean) {
  const router = useRouter();
  const { setAuth, setUser } = useAuthStore();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const poll = async () => {
      try {
        const res = await fetch("/api/customer/profile", {
          credentials: "include",
        });
        if (res.ok) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          intervalRef.current = null;
          const data = await res.json();
          setAuth();
          setUser({ id: data.id } as User);
          router.push("/home");
        }
      } catch {
        // Network error — keep polling
      }
    };

    intervalRef.current = setInterval(poll, 3000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, setAuth, setUser, router]);
}
